/**
 * Configuration defaults for frontend settings.
 * All values are sourced from environment variables for 100% dynamic configuration.
 * Fallback values are only used when environment variables are not set.
 * 
 * Environment Variables:
 * - VITE_DEFAULT_EMAIL_PROVIDER: Default email provider (default: 'SMTP')
 * - VITE_DEFAULT_SMTP_PORT: Default SMTP port (default: 587)
 * - VITE_DEFAULT_SMTP_ENCRYPTION: Default SMTP encryption (default: 'TLS')
 * - VITE_DEFAULT_SMTP_REJECT_UNAUTHORIZED: Whether to reject unauthorized certs (default: true)
 * - VITE_DEFAULT_SMS_PROVIDER: Default SMS provider name
 * - VITE_DEFAULT_SMS_SENDER_ID: Default SMS sender ID
 */

// Helper to parse boolean from env
const parseBoolean = (value: string | undefined, fallback: boolean): boolean => {
    if (value === undefined) return fallback;
    return value.toLowerCase() === 'true';
};

// Helper to parse number from env
const parseNumber = (value: string | undefined, fallback: number): number => {
    if (value === undefined) return fallback;
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
};

export const CONFIG_DEFAULTS = {
    EMAIL: {
        get PROVIDER() { return import.meta.env.VITE_DEFAULT_EMAIL_PROVIDER || 'SMTP'; },
        get SMTP_PORT() { return parseNumber(import.meta.env.VITE_DEFAULT_SMTP_PORT, 587); },
        get SMTP_SSL_PORT() { return parseNumber(import.meta.env.VITE_DEFAULT_SMTP_SSL_PORT, 465); },
        get ENCRYPTION() { return (import.meta.env.VITE_DEFAULT_SMTP_ENCRYPTION || 'TLS') as 'NONE' | 'SSL' | 'TLS'; },
        get REJECT_UNAUTHORIZED() { return parseBoolean(import.meta.env.VITE_DEFAULT_SMTP_REJECT_UNAUTHORIZED, true); },
    },
    SMS: {
        get PROVIDER() { return import.meta.env.VITE_DEFAULT_SMS_PROVIDER || ''; },
        get SENDER_ID() { return import.meta.env.VITE_DEFAULT_SMS_SENDER_ID || ''; },
    },
    SYSTEM_IDENTITY: {
        get UNIVERSITY_NAME() {
            return (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.VITE_DEFAULT_UNIVERSITY_NAME) ||
                   import.meta.env.VITE_DEFAULT_UNIVERSITY_NAME ||
                   '';
        },
        get SYSTEM_NAME() {
            return (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.VITE_DEFAULT_SYSTEM_NAME) ||
                   import.meta.env.VITE_DEFAULT_SYSTEM_NAME ||
                   'SFVS';
        },
        get LOGO_URL() {
            return (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.VITE_DEFAULT_LOGO_URL) ||
                   import.meta.env.VITE_DEFAULT_LOGO_URL ||
                   '';
        },
    },
} as const;
